import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { addEsChatToConfig } from "../../Utils/apiUtils";

/**
 * Role from /me.
 * - Super Admin | Admin | Team Member (All Labels) → userLabels = []
 * - Team Member → userLabels = ["vip", "urgent", ...] (can be empty)
 */
export type UserRole = string;

interface AuthState {
  loading?: boolean;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  error: string | boolean | undefined;
  system_user_id: string | null;
  /** Display name from GET /auth/me (`name` field) */
  userDisplayName: string | null;
  labels: Label[];
  /** User role from /me (e.g. "Super Admin", "Admin", "Team Member (All Labels)", "Team Member") */
  role: UserRole | null;
  /** Label names assigned to user from /me (e.g. ["vip", "urgent"]). Empty for Super Admin, Admin, Team Member (All Labels). */
  userLabels: string[];
}
export interface Label {
  id: number;
  label: string;
  user_id: string;
  keywords: string[];
  created_at: number;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isAuthenticating:
    typeof document !== "undefined" &&
    document.cookie.split(";").some((cookie) => cookie.trim().startsWith("es_chat=")),
  loading: false,
  error: false,
  system_user_id: null,
  userDisplayName: null,
  labels: [],
  role: null,
  userLabels: [],
};

export const fetchLabels = createAsyncThunk(
  "auth/fetchLabels",
  async (_, { rejectWithValue }) => {
    try {
      const config = addEsChatToConfig();
      
      const response = await axios.get(
        "https://api.insightssystem.com/api:bVXsw_FD/web/label_management",
        config
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const verifyAuthWithEsChat = createAsyncThunk(
  "auth/verifyAuthWithEsChat",
  async (_, { rejectWithValue }) => {
    try {
      // Get es_chat cookie
      const cookies = document.cookie.split(';');
      const esChatCookie = cookies.find(cookie => cookie.trim().startsWith('es_chat='));
      const token = esChatCookie ? esChatCookie.split('=')[1] : null;
      
      if (!token) {
        return rejectWithValue("No es_chat cookie found");
      }

      // Call /auth/me
      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/me', {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          user_id: data.user_id,
          email: data.email,
          id: data.id,
          name: typeof data.name === "string" ? data.name : null,
          role: data.role ?? null,
          labels: Array.isArray(data.labels) ? data.labels : []
        };
      } else {
        return rejectWithValue("Auth verification failed");
      }
    } catch (error) {
      return rejectWithValue("Auth verification failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login(state, action: PayloadAction<{ system_user_id: string; role?: UserRole | null; userLabels?: string[]; name?: string | null }>) {
      state.isAuthenticated = true;
      state.system_user_id = action.payload.system_user_id;
      if (action.payload.role !== undefined) state.role = action.payload.role ?? null;
      if (action.payload.userLabels !== undefined) state.userLabels = action.payload.userLabels ?? [];
      if (action.payload.name !== undefined) state.userDisplayName = action.payload.name ?? null;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.system_user_id = null;
      state.userDisplayName = null;
      state.role = null;
      state.userLabels = [];
    },
    addLabel(state, action: PayloadAction<Label>) {
      state.labels.push(action.payload);
    },
    removeLabel(state, action: PayloadAction<number>) {
      state.labels = state.labels.filter(
        (label) => label.id !== action.payload
      );
    },
    addKeywordToLabel(
      state,
      action: PayloadAction<{ labelId: number; newKeyword: string }>
    ) {
      const { labelId, newKeyword } = action.payload;
      const label = state.labels.find((label) => label.id === labelId);
      if (label) {
        label.keywords.push(newKeyword);
      }
    },
    removeKeywordFromLabel(
      state,
      action: PayloadAction<{ labelId: number; removedKeyword: string }>
    ) {
      const { labelId, removedKeyword } = action.payload;
      const label = state.labels.find((label) => label.id === labelId);

      if (label) {
        label.keywords = label.keywords.filter(
          (keyword) => keyword !== removedKeyword
        );
      }
    },
    setIsAuthenticating: (state, action) => {
      state.isAuthenticating = action.payload;
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabels.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchLabels.fulfilled,
        (state, action: PayloadAction<Label[]>) => {
          state.loading = false;
          state.labels = action.payload;
        }
      )
      .addCase(fetchLabels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyAuthWithEsChat.pending, (state) => {
        state.isAuthenticating = true;
      })
      .addCase(verifyAuthWithEsChat.fulfilled, (state, action) => {
        state.isAuthenticating = false;
        state.isAuthenticated = true;
        state.system_user_id = action.payload.user_id;
        state.userDisplayName = action.payload.name ?? null;
        state.role = action.payload.role ?? null;
        state.userLabels = Array.isArray(action.payload.labels) ? action.payload.labels : [];
      })
      .addCase(verifyAuthWithEsChat.rejected, (state) => {
        state.isAuthenticating = false;
        state.isAuthenticated = false;
        state.system_user_id = null;
        state.userDisplayName = null;
      });
  },
});

export const {
  login,
  logout,
  addLabel,
  removeLabel,
  addKeywordToLabel,
  removeKeywordFromLabel,
  setIsAuthenticating,
  setIsAuthenticated
} = authSlice.actions;
export const authReducer = authSlice.reducer;

// Selectors for thread (role/labels from /me)
export const selectRole = (state: { auth: AuthState }) => state.auth.role;
export const selectUserLabels = (state: { auth: AuthState }) => state.auth.userLabels;
export const selectUserDisplayName = (state: { auth: AuthState }) => state.auth.userDisplayName;
